import { shuttleData } from '../../data/shuttleData.js'
import { BYEONGJEOM_ROUTES } from '../../data/byeongjeomRoutes.js'
import { GOSAEK_ROUTES, ROUTE_STOP_MAP, buildTripRouteModel, getTripStopIds, shuttleRouteGroupKey } from '../../data/shuttle/index.js'
import { getCurrentMinutes, getNextShuttleTime } from './shuttleTimeUtils.js'

const ROUTE_DURATION_MINUTES = {
  songnae_to_school: 70,
  school_to_songnae: 70,
  gosaek_to_school: 25,
  gangnam_to_school: 75,
  sadang_to_school: 60,
  school_to_sadang: 60,
  byeongjeom_to_school: 40,
  school_to_byeongjeom: 35,
  campus_loop: 20,
}

function compactFareLabel(fare = '') {
  if (fare.includes('무료')) return '무료'
  const match = fare.match(/[\d,]+원/)
  return match ? match[0] : fare
}

function splitRouteName(name = '') {
  const [from = '', to = ''] = name.split('→').map(v => v.trim())
  return { from, to }
}

function getEstimatedMinutes(routeId, fallback = 40) {
  return ROUTE_DURATION_MINUTES[routeId] ?? fallback
}

function buildSchedules(route) {
  if (route.routeId === 'byeongjeom_to_school' && route.cmsManaged !== true) {
    return BYEONGJEOM_ROUTES
      .filter(r => r.direction === 'inbound')
      .map(r => ({
        id: r.id,
        time: r.time,
        buses: route.stops?.[0]?.buses?.[route.stops?.[0]?.times?.indexOf(r.time)] ?? 1,
        detailRouteId: r.id,
        durationSec: r.durationSec,
        destinationLabel: r.endName,
        routeSummary: r.summary,
      }))
  }

  if (route.routeId === 'school_to_byeongjeom' && route.cmsManaged !== true) {
    return BYEONGJEOM_ROUTES
      .filter(r => r.direction === 'outbound')
      .map(r => ({
        id: r.id,
        time: r.time,
        detailRouteId: r.id,
        durationSec: r.durationSec,
        destinationLabel: r.endName,
        routeSummary: r.summary,
      }))
  }

  const firstStop = route.stops?.[0]
  const durationSec = getEstimatedMinutes(route.routeId) * 60
  const schedules = (firstStop?.times ?? []).flatMap((time, index) => {
    if (route.routeId === 'gosaek_to_school' && time === '10:00') {
      return [
        {
          id: 'gosaek_1000_direct',
          time,
          displayTime: '10:00',
          detailRouteId: 'gosaek_1000_direct',
          stopIds: getTripStopIds(route.routeId, time, ROUTE_STOP_MAP[route.routeId]?.stopIds ?? [], { variant: 'direct' }),
          tripVariant: 'direct',
          durationSec,
          buses: firstStop?.buses?.[index],
          busType: firstStop?.busTypes?.[index] ?? null,
          charterStation: firstStop?.charterStations?.[index] ?? null,
          destinationLabel: ROUTE_STOP_MAP[route.routeId]?.displayStops ?? route.routeName,
          routeSummary: ROUTE_STOP_MAP[route.routeId]?.displayStops ?? firstStop?.location ?? route.routeName,
        },
        {
          id: 'gosaek_1000_campus_loop',
          time,
          displayTime: '10:00 순환',
          detailRouteId: 'gosaek_1000_campus_loop',
          stopIds: getTripStopIds(route.routeId, time, ROUTE_STOP_MAP[route.routeId]?.stopIds ?? [], { variant: 'campus_loop' }),
          tripVariant: 'campus_loop',
          durationSec: 30 * 60,
          buses: firstStop?.buses?.[index],
          busType: firstStop?.busTypes?.[index] ?? null,
          charterStation: firstStop?.charterStations?.[index] ?? null,
          destinationLabel: '고색역 → 교내 순환',
          routeSummary: '고색역 → 교내 순환',
        },
      ]
    }

    return [{
      id: `${route.routeId}_${time.replace(':', '')}`,
      time,
      displayTime: time,
      detailRouteId: `${route.routeId}_${time.replace(':', '')}`,
      stopIds: getTripStopIds(route.routeId, time, ROUTE_STOP_MAP[route.routeId]?.stopIds ?? []),
      durationSec,
      buses: firstStop?.buses?.[index],
      busType: firstStop?.busTypes?.[index] ?? null,
      charterStation: firstStop?.charterStations?.[index] ?? null,
      routeSummary: ROUTE_STOP_MAP[route.routeId]?.displayStops ?? firstStop?.location ?? route.routeName,
    }]
  })

  const additionalSchedules = (route.additionalSchedules ?? []).map(schedule => ({
    id: schedule.id,
    time: schedule.time,
    displayTime: schedule.displayTime ?? schedule.time,
    detailRouteId: schedule.id,
    stopIds: getTripStopIds(route.routeId, schedule.time, ROUTE_STOP_MAP[route.routeId]?.stopIds ?? []),
    tripVariant: schedule.tripVariant ?? null,
    durationSec,
    destinationLabel: schedule.destinationLabel ?? ROUTE_STOP_MAP[route.routeId]?.displayStops ?? route.routeName,
    routeSummary: schedule.routeSummary ?? schedule.destinationLabel ?? route.routeName,
  }))

  // 원본 시간 배열의 상대 순서는 유지하고, 추가 운행만 알맞은 위치에 삽입한다.
  return additionalSchedules.reduce((merged, additional) => {
    const insertAt = merged.findIndex(schedule => schedule.time > additional.time)
    merged.splice(insertAt === -1 ? merged.length : insertAt, 0, additional)
    return merged
  }, [...schedules])
}

export function getUnifiedShuttleRoutes(routes = shuttleData.routes) {
  return (routes ?? []).map(route => {
    const { from, to } = splitRouteName(route.routeName)
    const stopMap = ROUTE_STOP_MAP[route.routeId]
    const schedules = buildSchedules(route)
    const firstStop = route.stops?.[0]
    const defaultStopIds = getTripStopIds(route.routeId, schedules[0]?.time, stopMap?.stopIds ?? [])
    const defaultTripModel = buildTripRouteModel(route.routeId, schedules[0]?.time, defaultStopIds)

    return {
      id: route.routeId,
      name: route.routeName,
      from,
      to,
      fareLabel: compactFareLabel(route.fare),
      boardingPoint: firstStop?.stopName ?? stopMap?.displayStops ?? from,
      boardingPointDetail: firstStop?.location ?? '',
      destinationLabel: stopMap?.displayStops ?? route.routeName,
      schedules,
      estimatedMinutes: getEstimatedMinutes(route.routeId),
      stopsCount: Math.max(defaultStopIds.length, stopMap?.stopIds?.length ?? 1),
      routeSummary: stopMap?.displayStops ?? firstStop?.location ?? route.routeName,
      routeStops: defaultTripModel.stops,
      mapRouteData: defaultStopIds.length >= 2,
      boardingStopId: stopMap?.boardingStopId,
      original: route,
    }
  })
}

export function getShuttleDetailRoutes(routes = shuttleData.routes) {
  const detailRoutes = []

  for (const route of getUnifiedShuttleRoutes(routes)) {
    for (const schedule of route.schedules) {
      const existingByeongjeom = BYEONGJEOM_ROUTES.find(r => r.id === schedule.detailRouteId)
      if (existingByeongjeom) {
        // TODO: 병점역도 stopIds + segment geometry 모델로 이관한다. 현재 PR에서는 기존 동작 보존.
        detailRoutes.push({ ...existingByeongjeom, routeKey: route.id, fareLabel: route.fareLabel })
        continue
      }

      const existingGosaek = GOSAEK_ROUTES.find(r => r.id === schedule.detailRouteId)
      if (existingGosaek) {
      const model = buildTripRouteModel(route.id, schedule.time, schedule.stopIds, { variant: schedule.tripVariant })
      detailRoutes.push({
        ...existingGosaek,
          routeKey: route.id,
          fareLabel: route.fareLabel,
          stopIds: model.stopIds,
          stops: model.stops,
          path: model.path,
          composedPath: model.composedPath,
          roadReference: model.roadReference,
        })
        continue
      }

      const model = buildTripRouteModel(route.id, schedule.time, schedule.stopIds, { variant: schedule.tripVariant })
      const stops = model.stops
      const durationSec = schedule.durationSec ?? route.estimatedMinutes * 60
      detailRoutes.push({
        id: schedule.detailRouteId,
        routeKey: route.id,
        stopIds: model.stopIds,
        label: schedule.displayTime ?? schedule.time,
        displayTime: schedule.displayTime ?? schedule.time,
        fullLabel: `${route.name} ${schedule.displayTime ?? schedule.time}`,
        time: schedule.time,
        departureTime: schedule.time,
        routeName: schedule.tripVariant === 'campus_loop' ? `${route.name} 순환` : route.name,
        fareLabel: route.fareLabel,
        startName: stops[0]?.name ?? route.from,
        endName: stops[stops.length - 1]?.name ?? route.to,
        durationText: `약 ${Math.round(durationSec / 60)}분`,
        durationSec,
        direction: route.id.startsWith('school_to_') ? 'outbound' : route.id === 'campus_loop' ? 'loop' : 'inbound',
        summary: schedule.routeSummary,
        destinationLabel: schedule.destinationLabel ?? stops[stops.length - 1]?.name ?? route.to,
        tripVariant: schedule.tripVariant ?? null,
        notice: route.boardingPointDetail || `${route.name} 노선입니다.`,
        stops,
        path: model.path,
        composedPath: model.composedPath,
        roadReference: model.roadReference,
      })
    }
  }

  return detailRoutes
}

export function getDetailRouteGroup(routeId, routes = shuttleData.routes) {
  const detailRoutes = getShuttleDetailRoutes(routes)
  const selected = detailRoutes.find(route => route.id === routeId)
    ?? detailRoutes.find(route => route.routeKey === routeId)
    ?? detailRoutes[0]
    ?? null
  if (!selected) return { selected: null, group: [] }

  const selectedGroupKey = shuttleRouteGroupKey(selected)

  return {
    selected,
    group: detailRoutes.filter(route => shuttleRouteGroupKey(route) === selectedGroupKey),
  }
}

export function getDefaultShuttleDetailRouteId(routes = shuttleData.routes, options = {}) {
  const detailRoutes = getShuttleDetailRoutes(routes)
  if (options.preferredRouteId && detailRoutes.some(route => route.id === options.preferredRouteId)) {
    return options.preferredRouteId
  }

  const groupKey = options.routeKey ?? 'byeongjeom_to_school'
  const group = detailRoutes.filter(route => shuttleRouteGroupKey(route) === groupKey)
  const candidates = group.length > 0 ? group : detailRoutes
  if (candidates.length === 0) return null

  const now = options.now ?? getCurrentMinutes()
  const nextTime = getNextShuttleTime(candidates.map(route => route.time).filter(Boolean), now)?.time
  return candidates.find(route => route.time === nextTime)?.id
    ?? candidates[0]?.id
    ?? null
}
